import type {
  FundStatement,
  FundStatementInstance,
  SubPortfolioFundVersion,
} from '@festina/unitlink-general-types/unitlink'
import {
  assertNotEmpty,
  dateUtils,
  notEmpty,
  type Optional,
} from '@festina-life/core'

type AggregatedFundStatements = {
  fundStatements: Optional<(FundStatement | null)[]>
  calculationMethod?: (
    fundStatementInstance: FundStatementInstance | null,
  ) => number
  label: string
}

export const aggregateFundStatements = ({
  calculationMethod = (fundStatementInstance) =>
    fundStatementInstance?.cashValue ?? 0,
  fundStatements,
  label,
  shouldRemoveYieldFromStatement = false,
}: AggregatedFundStatements & { shouldRemoveYieldFromStatement?: boolean }) => {
  const aggregatedFundStatements = fundStatements?.reduce<{
    fundName: string
    information: { date: Optional<string>; value: number }[]
  }>(
    (acc, fundStatement) => {
      fundStatement?.fundStatementInstances?.forEach(
        (fundStatementInstance) => {
          const date = fundStatementInstance?.date
          const cashValue = fundStatementInstance?.cashValue
          const existing = acc.information.find((z) => z.date === date)

          if (existing) {
            if (shouldRemoveYieldFromStatement) {
              existing.value +=
                (cashValue ?? 0) - (fundStatementInstance?.yield ?? 0)
            } else {
              existing.value += cashValue ?? 0
            }
          } else {
            acc.information.push({
              date,
              value: shouldRemoveYieldFromStatement
                ? calculationMethod(fundStatementInstance) -
                  (fundStatementInstance?.yield ?? 0)
                : calculationMethod(fundStatementInstance),
            })
          }
        },
      )

      return acc
    },
    { fundName: label, information: [] },
  )
  const aggregatedFundStatementsWithSortedDates = {
    fundName: aggregatedFundStatements?.fundName,
    information: aggregatedFundStatements?.information?.sort((a, b) =>
      dateUtils.compare(a.date, b.date),
    ),
  }

  return [aggregatedFundStatementsWithSortedDates]
}

export const aggregateYieldFundStatements = ({
  fundStatements,
  label,
}: AggregatedFundStatements) => {
  const aggregatedFundStatements = fundStatements?.reduce<{
    fundName: string
    information: {
      date: Optional<string>
      value: number
    }[]
  }>(
    (acc, fundStatement) => {
      fundStatement?.fundStatementInstances?.forEach(
        (fundStatementInstance) => {
          const fundStatementInstanceDate = fundStatementInstance?.date
          const fundStatementInstanceYield = fundStatementInstance?.yield
          const existing = acc.information.find(
            ({ date }) => date === fundStatementInstanceDate,
          )

          if (existing) {
            existing.value += fundStatementInstanceYield ?? 0
          } else {
            acc.information.push({
              date: fundStatementInstanceDate,
              value: fundStatementInstanceYield ?? 0,
            })
          }
        },
      )

      return acc
    },
    { fundName: label, information: [] },
  )
  const aggregatedFundStatementsWithSortedDates = {
    fundName: aggregatedFundStatements?.fundName,
    information: aggregatedFundStatements?.information?.sort((a, b) =>
      dateUtils.compare(a.date, b.date),
    ),
  }

  return [aggregatedFundStatementsWithSortedDates]
}

export const formatUnit = (unit: Optional<number>) => unit?.toFixed(6)

export const hasAmountOnCover = (
  subPortfolioFundVersions:
    | SubPortfolioFundVersion[]
    | SubPortfolioFundVersion[],
) =>
  subPortfolioFundVersions?.some(
    (x) =>
      x?.cashValueSpecAtApplyTime?.balance !== 0 ||
      x?.cashValueSpecAtApplyTime?.reservedBalance !== 0,
  )

export const calculateYieldForYear = (
  fundStatements: Optional<(FundStatement | null)[]>,
  time: Optional<string>,
) => {
  assertNotEmpty(time)

  const currentYear = new Date(time).getFullYear()
  const previousYear = currentYear - 1

  const fundStatementInstances = fundStatements
    ?.flatMap((fundStatement) => fundStatement?.fundStatementInstances)
    .filter(notEmpty)

  const yieldForCurrentYear = getYield(fundStatementInstances, currentYear)

  const yieldForLastYear = getYield(fundStatementInstances, previousYear)

  return yieldForCurrentYear - yieldForLastYear
}

const getYield = (
  fundStatementInstances: Optional<FundStatementInstance[]>,
  year: number,
) => {
  const statementsForYear =
    fundStatementInstances?.filter((fundStatementInstance) => {
      assertNotEmpty(fundStatementInstance?.date)

      return new Date(fundStatementInstance.date).getFullYear() === year
    }) ?? []

  return statementsForYear.reduce(
    (acc, fundStatementInstance) => acc + (fundStatementInstance.yield ?? 0),
    0,
  )
}
